import dayjs from "dayjs";
import { cloneDeep } from "lodash";

import {
  Condition,
  ConditionElementType,
  ElementStatus,
  ElementType,
  Form,
  FormElement,
  FormElse,
  FormIf,
  isText,
  isDate,
  LogicalType,
} from "@/@types/entities/forms";
import { ConditionManager, Option } from "@/@types/entities/forms/manager";

class FormConditionsManager implements ConditionManager {
  getInputOptionsByElement(
    element: ConditionElementType,
    inputList?: FormElement[],
    currentCondition?: Condition
  ): Option[] {
    const disabledIfInputTypes = [ElementType.Header, ElementType.Reminder, ElementType.Pdp];

    const options =
      inputList
        ?.filter((input) => input.status === ElementStatus.Active)
        ?.map<Option>((input) => {
          const disabled = element === "if" ? disabledIfInputTypes.includes(input.type) : false;

          const option: Option = {
            name: input.name,
            label: input.label,
            type: input.type,
            validation: isText(input) ? input.validation : null,
            is_range: isDate(input) ? input.is_range : null,
            disabled,
          };

          return option;
        }) || [];

    if (element === "if") return options;

    /**
     * If the element is an "ELSE", filters the inputs option to only show the inputs below the lower element in if section
     */
    const ifInputsNames = currentCondition?.form_if.map((ifItem) => ifItem.input_name);

    if (!ifInputsNames) return options;

    const namesIndexes = ifInputsNames.map((ifName) =>
      options.findIndex((opt) => opt.name === ifName)
    );

    const maxIndex = Math.max(...namesIndexes);

    if (maxIndex === -1) return [];

    const elseOptions = options.slice(maxIndex + 1);

    return elseOptions;
  }

  getElementId(element: ConditionElementType): string {
    // const timestamp = dayjs().unix();
    const timestamp = dayjs().valueOf();
    
    return `${element}-${timestamp}`;
  }

  getConditionBody(id: string, form?: Form): Condition {
    const foundCondition = form?.conditionals?.find((condition) => condition.id === id);
    const baseCondition: Condition = {
      id,
      logical_type: LogicalType.Anyone,
      form_if: [
        {
          id: this.getElementId("if"),
        },
      ],
      form_else: [
        {
          id: this.getElementId("else"),
        },
      ],
    };

    if (!foundCondition) return cloneDeep(baseCondition);

    return cloneDeep(foundCondition);
  }

  addElement(element: ConditionElementType, id: string, condition: Condition): Condition {
    const currentCondition = condition;

    let foundIndex = undefined;

    if (element === "if") {
      foundIndex = currentCondition.form_if.findIndex((ifElement) => ifElement.id === id);
      currentCondition.form_if.splice(foundIndex + 1, 0, { id: this.getElementId("if") });
    }

    if (element === "else") {
      foundIndex = currentCondition.form_else.findIndex((elseElement) => elseElement.id === id);
      currentCondition.form_else.splice(foundIndex + 1, 0, { id: this.getElementId("else") });
    }

    return currentCondition;
  }

  removeElement(element: ConditionElementType, id: string, condition: Condition): Condition {
    const currentCondition = condition;

    let foundIndex = undefined;

    if (element === "if") {
      foundIndex = currentCondition.form_if.findIndex((ifElement) => ifElement.id === id);
      currentCondition.form_if.splice(foundIndex, 1);
    }

    if (element === "else") {
      foundIndex = currentCondition.form_else.findIndex((elseElement) => elseElement.id === id);
      currentCondition.form_else.splice(foundIndex, 1);
    }

    return currentCondition;
  }

  updateElement(
    element: ConditionElementType,
    form: FormIf | FormElse,
    condition: Condition
  ): Condition {
    const currentCondition = condition;

    let foundIndex = undefined;

    if (element === "if") {
      foundIndex = currentCondition.form_if.findIndex((ifElement) => ifElement.id === form.id);
      currentCondition.form_if[foundIndex] = form;
    }

    if (element === "else") {
      foundIndex = currentCondition.form_else.findIndex(
        (elseElement) => elseElement.id === form.id
      );
      currentCondition.form_else[foundIndex] = form;
    }

    return currentCondition;
  }

  removeCondition(id: string, formConditions: Condition[] = []): Condition[] {
    const formConditionsCopy = cloneDeep(formConditions);

    const foundIndex = formConditionsCopy.findIndex((formCondition) => formCondition.id === id);

    if (foundIndex !== -1) formConditionsCopy.splice(foundIndex, 1);

    return formConditionsCopy;
  }

  buildConditionsArray(
    newCondition: Condition,
    formConditions: Condition[] | null = []
  ): Condition[] {
    const formConditionsCopy = formConditions ? cloneDeep(formConditions) : [];

    const foundIndex = formConditionsCopy.findIndex(
      (formCondition) => formCondition.id === newCondition.id
    );

    if (foundIndex === -1) return [newCondition, ...formConditionsCopy];

    formConditionsCopy[foundIndex] = newCondition;

    return formConditionsCopy;
  }
}

export const formConditionsManager = new FormConditionsManager();
